import type { FormDto } from "../../types/form";
import { parseBoolean } from "../forms/parseBoolean";

export function parseHookEnabled(
  raw: Record<string, unknown>,
  defaultValue = true,
): boolean {
  if (!("enabled" in raw)) return defaultValue;
  return parseBoolean(raw.enabled);
}

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(Math.trunc(value), min), max);
}

function normalizeRetry(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const item = raw as Record<string, unknown>;
  return {
    enabled: "enabled" in item ? parseBoolean(item.enabled) : false,
    maxAttempts: clampNumber(
      Number(item.maxAttempts ?? item.max_attempts ?? 3),
      1,
      7,
    ),
    delayMs: clampNumber(
      Number(item.delayMs ?? item.delay_ms ?? 2000),
      500,
      60000,
    ),
  };
}

function normalizeFioSplit(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;

  const result: Record<string, unknown> = {};
  for (const [source, ruleRaw] of Object.entries(
    raw as Record<string, unknown>,
  )) {
    if (!ruleRaw || typeof ruleRaw !== "object" || Array.isArray(ruleRaw))
      continue;
    const rule = ruleRaw as Record<string, unknown>;
    const parts = rule.parts;
    result[source] = {
      splitBySpace: parseBoolean(
        rule.splitBySpace ?? rule.split_by_space ?? false,
      ),
      parts:
        parts && typeof parts === "object" && !Array.isArray(parts)
          ? parts
          : {},
    };
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

function normalizeHookMethod(raw: unknown): string {
  const method = String(raw ?? "POST").toUpperCase();
  return ["GET", "POST", "PUT", "PATCH", "DELETE"].includes(method)
    ? method
    : "POST";
}

function normalizeHookFormat(raw: unknown): string {
  const format = String(raw ?? "json").toLowerCase();
  return ["json", "form", "query"].includes(format) ? format : "json";
}

function normalizeHookMode(
  raw: unknown,
  bodyTemplate: string | undefined,
): "template" | "mapping" {
  const mode = String(raw ?? "").toLowerCase();
  if (mode === "template" || mode === "mapping") return mode;
  return bodyTemplate ? "template" : "mapping";
}

/** Читает хуки из нового и legacy-ключей формы. */
export function resolveSubmitHooksFromForm(form: FormDto): unknown[] {
  const candidates = [
    form.submitHooks,
    form.submit_hooks,
    form.submit_hook,
  ].filter((value) => value != null);

  for (const value of candidates) {
    if (Array.isArray(value) && value.length > 0) return value;
  }

  for (const value of candidates) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") return [value];
  }

  return [];
}

export function normalizeSubmitHookForEditor(item: unknown): unknown {
  if (!item || typeof item !== "object") return item;
  const raw = item as Record<string, unknown>;
  const normalized: Record<string, unknown> = { ...raw };

  const aliases: Array<[camel: string, snake: string]> = [
    ["bodyTemplate", "body_template"],
    ["fieldMapping", "field_mapping"],
    ["fioSplit", "fio_split"],
    ["sendDeliveryId", "send_delivery_id"],
    ["timeoutSeconds", "timeout_seconds"],
  ];

  for (const [camel, snake] of aliases) {
    if (normalized[camel] == null && normalized[snake] != null) {
      normalized[camel] = normalized[snake];
    }
    delete normalized[snake];
  }

  const bodyTemplate =
    typeof normalized.bodyTemplate === "string" &&
    normalized.bodyTemplate.trim() !== ""
      ? normalized.bodyTemplate
      : undefined;

  normalized.url =
    typeof normalized.url === "string" ? normalized.url.trim() : "";
  normalized.method = normalizeHookMethod(normalized.method);
  normalized.format = normalizeHookFormat(normalized.format);
  normalized.mode = normalizeHookMode(normalized.mode, bodyTemplate);
  normalized.enabled = parseHookEnabled(raw);
  normalized.sendDeliveryId =
    "sendDeliveryId" in normalized
      ? parseBoolean(normalized.sendDeliveryId)
      : true;
  normalized.timeoutSeconds = clampNumber(
    Number(normalized.timeoutSeconds ?? 5),
    1,
    30,
  );

  if (bodyTemplate) {
    normalized.bodyTemplate = bodyTemplate;
  } else {
    delete normalized.bodyTemplate;
  }

  const fieldMapping = normalized.fieldMapping;
  normalized.fieldMapping =
    fieldMapping &&
    typeof fieldMapping === "object" &&
    !Array.isArray(fieldMapping)
      ? fieldMapping
      : {};

  const headers = normalized.headers;
  normalized.headers =
    headers && typeof headers === "object" && !Array.isArray(headers)
      ? headers
      : undefined;

  const fioSplit = normalizeFioSplit(normalized.fioSplit);
  if (fioSplit) {
    normalized.fioSplit = fioSplit;
  } else {
    delete normalized.fioSplit;
  }

  const retry = normalizeRetry(normalized.retry);
  if (retry) {
    normalized.retry = retry;
  } else {
    delete normalized.retry;
  }

  return normalized;
}

/** Новый camelCase-формат для `submitHooks` в PATCH/POST. */
export function normalizeSubmitHookForSave(
  item: unknown,
): Record<string, unknown> | null {
  const normalized = normalizeSubmitHookForEditor(item) as Record<
    string,
    unknown
  >;
  if (!normalized || typeof normalized !== "object") return null;

  const url = typeof normalized.url === "string" ? normalized.url.trim() : "";
  if (!url) return null;

  const payload: Record<string, unknown> = {
    url,
    method: normalized.method,
    format: normalized.format,
    mode: normalized.mode,
    enabled: normalized.enabled,
    sendDeliveryId: normalized.sendDeliveryId,
    timeoutSeconds: normalized.timeoutSeconds,
    fieldMapping: normalized.fieldMapping ?? {},
  };

  if (typeof normalized.bodyTemplate === "string") {
    payload.bodyTemplate = normalized.bodyTemplate;
  }

  if (
    normalized.headers &&
    typeof normalized.headers === "object" &&
    !Array.isArray(normalized.headers) &&
    Object.keys(normalized.headers as object).length > 0
  ) {
    payload.headers = normalized.headers;
  }

  if (normalized.fioSplit) {
    payload.fioSplit = normalized.fioSplit;
  }

  if (normalized.retry) {
    payload.retry = normalized.retry;
  }

  return payload;
}

export function normalizeSubmitHooksForSave(hooks: unknown[]): unknown[] {
  return hooks
    .map(normalizeSubmitHookForSave)
    .filter((item): item is Record<string, unknown> => item != null);
}

export function submitHooksTextFromForm(hooks: unknown[]): string {
  const normalized = hooks.map(normalizeSubmitHookForEditor);
  return JSON.stringify(normalized, null, 2);
}

/** После save API может не вернуть enabled — сохраняем локальное состояние по URL. */
export function mergeSubmitHooksTextAfterSave(
  beforeText: string,
  afterText: string,
): string {
  try {
    const before = JSON.parse(beforeText || "[]") as unknown;
    const after = JSON.parse(afterText || "[]") as unknown;
    if (!Array.isArray(before) || !Array.isArray(after)) return afterText;

    const enabledByUrl = new Map<string, boolean>();
    for (const hook of before) {
      if (!hook || typeof hook !== "object") continue;
      const record = hook as Record<string, unknown>;
      const url = typeof record.url === "string" ? record.url.trim() : "";
      if (url && "enabled" in record) {
        enabledByUrl.set(url, parseBoolean(record.enabled));
      }
    }

    const merged = after.map((hook) => {
      if (!hook || typeof hook !== "object") return hook;
      const record = { ...(hook as Record<string, unknown>) };
      const url = typeof record.url === "string" ? record.url.trim() : "";

      if (url && enabledByUrl.has(url)) {
        record.enabled = enabledByUrl.get(url);
      } else if ("enabled" in record) {
        record.enabled = parseBoolean(record.enabled);
      }

      return record;
    });

    return submitHooksTextFromForm(merged);
  } catch {
    return afterText;
  }
}
